/**
 * Slash command handlers for Ragsmith Web UI
 */

import { state, setTemperature, setDebugMode, resetNarratorState, clearSessionStorage } from './state.js';
import { elements, addSystemMessage, addCommandOutput, clearChat, updateModeIndicators, showCollectionHeader } from './ui.js';
import { updateMenuLoadButton, updateHeaderStatus } from './menu.js';
import * as api from './api.js';
import { compactNow, currentContext } from './context-meter.js';

/**
 * Command handlers object
 */
export const commands = {
    help: () => {
        const help = `/help - Show this help
/clear - Clear chat history
/new - Start new session

/agent [name] - Switch agent or show current
/model [tier] - Switch model or show current
/temp [0.0-2.0] - Set temperature (lower=focused, higher=creative)
/debug - Toggle debug mode (shows routing/search info)

/narrator on|off - Toggle narrator mode
/narrator reset - Reset to chunk 1
/narrator status - Show narrator mode status
/narrator books - List available books

/memory clear - Clear agent memory (resets fields)
/compact [focus] - Summarize the older turns now (optionally say what to keep)
/context - How full the context is

/status - Show system status`;
        addCommandOutput(help);
    },

    temp: (args) => {
        if (!args) {
            const current = state.temperature !== null ? state.temperature : 'default (0.7)';
            addCommandOutput(`Current temperature: ${current}\nUsage: /temp <0.0-2.0> (lower=focused, higher=creative)\nUse /temp reset to restore default`);
            return;
        }
        if (args === 'reset' || args === 'default') {
            setTemperature(null);
            addSystemMessage('Temperature reset to default (0.7)');
            return;
        }
        const val = parseFloat(args);
        if (isNaN(val) || val < 0 || val > 2) {
            addCommandOutput('Temperature must be between 0.0 and 2.0');
            return;
        }
        setTemperature(val);
        addSystemMessage(`Temperature set to ${val} (persisted)`);
    },

    debug: () => {
        setDebugMode(!state.debugMode);
        addSystemMessage(`Debug mode: ${state.debugMode ? 'ON - routing/search info will be shown' : 'OFF'}`);
    },

    compact: async (args) => {
        const result = await compactNow(args || '');
        if (result) {
            addCommandOutput(`Compacted ${result.new_messages} messages (${result.covered_messages} in the summary so far). Context ${Math.round(result.percent_before)}% to ${Math.round(result.percent_after)}%.`);
        }
    },

    context: async () => {
        if (!state.sessionId) {
            addCommandOutput('No conversation yet: the context is empty.');
            return;
        }
        let ctx = currentContext();
        try {
            ctx = await api.fetchSessionContext(state.sessionId, { memoryLevel: state.memoryLevel });
        } catch (e) {
            if (!ctx) {
                addCommandOutput(`Could not read the context: ${e.message}`);
                return;
            }
        }
        const k = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
        const lines = [
            `Context: about ${k(ctx.used_tokens)} of ${k(ctx.usable_tokens)} usable tokens (${Math.round(ctx.percent)}%), model window ${k(ctx.window_tokens)}, ${ctx.model}`,
            `Messages sent each turn: ${ctx.history_messages}` + (ctx.dropped_messages ? `, ${ctx.dropped_messages} older beyond the Memory window` : ''),
            ctx.last_compaction ? `Summary: ${ctx.last_compaction.covered_messages} messages, ~${k(ctx.summary_tokens)} tokens` : 'Not compacted yet.',
            state.autoCompact === false ? 'Auto-compact: off' : ctx.auto_paused ? 'Auto-compact: paused after 3 failures' : `Auto-compact: on, at ${ctx.threshold_percent}%`,
        ];
        addCommandOutput(lines.join('\n'));
    },

    clear: () => {
        clearChat();
        addSystemMessage('Chat cleared.');
    },

    new: () => {
        state.sessionId = null;
        resetNarratorState();
        clearSessionStorage();
        clearChat();

        // Preserve loaded agent/collection - just start fresh conversation
        updateModeIndicators(state.loadedAgent, state.loadedCollectionTitle, false, 1);
        updateMenuLoadButton();
        updateHeaderStatus();

        // Show collection header if a collection is loaded
        if (state.loadedCollection && state.loadedCollectionTitle) {
            showCollectionHeader(state.loadedCollectionTitle, state.loadedCollectionDescription);
        }

        addSystemMessage('Started new session.');
    },

    agent: async (args) => {
        if (!args) {
            addCommandOutput(`Current agent: ${state.currentAgent || '(none)'}`);
            return;
        }
        const options = Array.from(elements.agentSelect.options);
        const match = options.find(o => o.value.toLowerCase() === args.toLowerCase());
        if (match) {
            elements.agentSelect.value = match.value;
            state.currentAgent = match.value;
            state.loadedAgent = null; // Reset loaded state
            updateMenuLoadButton(match.value, null);
            updateHeaderStatus();
            addSystemMessage(`Switched to agent: ${state.currentAgent}`);
            // Reset narrator mode when switching agents
            if (state.narratorMode) {
                resetNarratorState();
                updateModeIndicators(state.loadedAgent, state.loadedCollection, false, 1);
            }
        } else {
            addCommandOutput(`Agent not found: ${args}\nAvailable: ${options.map(o => o.value).filter(v => v).join(', ')}`);
        }
    },

    model: (args) => {
        if (!args) {
            addCommandOutput(`Current model: ${elements.modelSelect.value || 'default'}`);
            return;
        }
        const options = Array.from(elements.modelSelect.options);
        const match = options.find(o => o.value.toLowerCase() === args.toLowerCase());
        if (match) {
            elements.modelSelect.value = match.value;
            updateHeaderStatus();
            addSystemMessage(`Switched to model: ${match.value}`);
        } else {
            addCommandOutput(`Model tier not found: ${args}\nAvailable: ${options.map(o => o.value).filter(v => v).join(', ')}`);
        }
    },

    narrator: async (args) => {
        if (!state.currentAgent) {
            addCommandOutput('Narrator mode requires an agent. Use /agent <name> first.');
            return;
        }

        const subCmd = args?.split(' ')[0]?.toLowerCase();

        if (subCmd === 'on') {
            state.narratorMode = true;
            state.narratorState = { book: null, kb_path: null, chunk_id: 1, chunk_batch: 1, narration_length: 1 };
            // Fetch first book for this agent
            try {
                const data = await api.fetchAgentBooks(state.currentAgent);
                if (data.books?.length > 0) {
                    state.narratorState.book = data.books[0].name;
                }
            } catch (e) {
                // Silently continue - server will pick default book
            }
            updateModeIndicators(state.loadedAgent, state.loadedCollection, true, state.narratorState.chunk_id);
            // Update web search button (disabled in narrator mode)
            window.updateWebSearchButtonState?.();
            // Update delete buttons (only most recent deletable in narrator mode)
            window.updateNarratorDeleteButtons?.();
            // Update voice button icon
            import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                updateVoiceBtnIcon(true, !!state.agentSettings?.narrator_verbatim);
            }).catch(() => {});
            addSystemMessage('Narrator mode enabled. Web search disabled.');
        } else if (subCmd === 'off') {
            state.narratorMode = false;
            updateModeIndicators(state.loadedAgent, state.loadedCollection, false, 1);
            // Update web search button (re-enabled)
            window.updateWebSearchButtonState?.();
            // Update delete buttons (all deletable when not in narrator mode)
            window.updateNarratorDeleteButtons?.();
            // Reset voice button icon
            import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                updateVoiceBtnIcon(false, false);
            }).catch(() => {});
            addSystemMessage('Narrator mode disabled. Web search enabled.');
        } else if (subCmd === 'reset') {
            try {
                await api.resetNarratorProgress(state.currentAgent);
                state.narratorState.chunk_id = 1;
                state.narratorState.kb_path = null;
                updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, 1);
                addSystemMessage('Narrator progress reset to beginning.');
            } catch (e) {
                addSystemMessage('Failed to reset narrator progress.');
            }
        } else if (subCmd === 'status') {
            addCommandOutput(`Narrator mode: ${state.narratorMode ? 'ON' : 'OFF'}
Book: ${state.narratorState.book || 'none'}
KB: ${state.narratorState.kb_path || 'auto'}
Position: chunk ${state.narratorState.chunk_id}`);
        } else if (subCmd === 'books') {
            try {
                const data = await api.fetchAgentBooks(state.currentAgent);
                if (data.books?.length > 0) {
                    const list = data.books.map(b => `  ${b.name} (${b.kb_count} sections)`).join('\n');
                    addCommandOutput(`Books for ${state.currentAgent}:\n${list}`);
                } else {
                    addCommandOutput('No books found for this agent.');
                }
            } catch (e) {
                addCommandOutput('Could not load books.');
            }
        } else {
            addCommandOutput('Usage: /narrator on|off|reset|status|books');
        }
    },

    status: async () => {
        try {
            const data = await api.checkStatus();
            const tempStr = state.temperature !== null ? state.temperature : 'default (0.7)';
            addCommandOutput(`Status: ${data.status}
KBs: ${data.kb_count}
Agents: ${data.agent_count}
Models: ${data.model_count}
Ollama: ${data.ollama_host}
Default model: ${data.default_model}
Temperature: ${tempStr}`);
        } catch (e) {
            addCommandOutput('Could not fetch status.');
        }
    },

    memory: async (args) => {
        if (!state.currentAgent) {
            addSystemMessage('No agent loaded.');
            return;
        }
        const subCmd = args?.toLowerCase();
        if (subCmd === 'clear') {
            try {
                const data = await api.clearAgentMemory(state.currentAgent);
                state.pendingFields = [];
                addSystemMessage(data.message);
            } catch (e) {
                addSystemMessage('Failed to clear memory.');
            }
        } else {
            addCommandOutput('Usage: /memory clear');
        }
    }
};

/**
 * Handle a slash command
 * @returns {boolean} True if command was handled
 */
export async function handleCommand(input) {
    const match = input.match(/^\/(\w+)\s*(.*)?$/);
    if (!match) return false;

    const [, cmd, args] = match;
    const handler = commands[cmd.toLowerCase()];

    if (handler) {
        await handler(args?.trim());
        return true;
    } else {
        addCommandOutput(`Unknown command: /${cmd}\nType /help for available commands.`);
        return true;
    }
}
